import { createFileRoute, Link, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listYardTrailers, upsertYardTrailer, setYardStatus, deleteYardTrailer,
} from "@/lib/yard.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import {
  Plus, Container, Snowflake, ArrowDown, ArrowUp, LogOut, MapPin, DoorOpen, Trash2, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { DockYardTabs } from "@/components/dock-yard-tabs";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";

export const Route = createFileRoute("/_authenticated/app/yard")({
  validateSearch: zodValidator(dateRangeSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: YardPage,
});

type YardRow = {
  id: string;
  facility_id: string;
  yard_spot: string | null;
  trailer_number: string;
  carrier: string | null;
  seal_number: string | null;
  kind: "inbound" | "outbound";
  status: "arrived" | "parked" | "at_door" | "loading" | "unloading" | "loaded" | "empty" | "departed";
  temp_setpoint_f: number | null;
  asn_id: string | null;
  shipment_id: string | null;
  dock_door: string | null;
  driver_name: string | null;
  driver_phone: string | null;
  arrived_at: string;
  departed_at: string | null;
  notes: string | null;
};

const STATUS_OPTIONS: YardRow["status"][] = [
  "arrived", "parked", "at_door", "loading", "unloading", "loaded", "empty", "departed",
];

function fmtDuration(fromIso: string) {
  const ms = Date.now() - new Date(fromIso).getTime();
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (h > 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function YardPage() {
  const { tenantId, facilityId } = useScope();
  const qc = useQueryClient();
  const urlSearch = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const sort = urlSearch.sort ?? "desc";
  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: urlSearch.start, end: urlSearch.end }),
    [urlSearch.start, urlSearch.end],
  );
  const listFn = useServerFn(listYardTrailers);
  const saveFn = useServerFn(upsertYardTrailer);
  const statusFn = useServerFn(setYardStatus);
  const delFn = useServerFn(deleteYardTrailer);

  const [kindFilter, setKindFilter] = useState<"all" | "inbound" | "outbound">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | YardRow["status"]>("all");
  const [includeDeparted, setIncludeDeparted] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<YardRow | null>(null);

  const queryKey = ["yard-trailers", tenantId, facilityId, kindFilter, statusFilter, includeDeparted, fromIso, toIso, sort];
  const { data, isLoading } = useQuery({
    queryKey,
    enabled: !!tenantId,
    queryFn: () => listFn({ data: {
      tenantId: tenantId!,
      facilityId: facilityId ?? undefined,
      kind: kindFilter === "all" ? undefined : kindFilter,
      status: statusFilter === "all" ? undefined : statusFilter,
      includeDeparted,
      fromIso, toIso, sort,
    } }),
  });

  const rows: YardRow[] = (data?.rows ?? []) as YardRow[];

  const totals = useMemo(() => ({
    onYard: rows.filter((r) => !r.departed_at).length,
    inbound: rows.filter((r) => r.kind === "inbound" && !r.departed_at).length,
    outbound: rows.filter((r) => r.kind === "outbound" && !r.departed_at).length,
    atDoor: rows.filter((r) => r.status === "at_door" || r.status === "loading" || r.status === "unloading").length,
    reefer: rows.filter((r) => r.temp_setpoint_f != null && !r.departed_at).length,
  }), [rows]);

  const groups = useMemo(() => {
    const m = new Map<string, YardRow[]>();
    for (const r of rows) {
      let key: string;
      if (r.departed_at) key = "Z · Departed";
      else if (r.status === "at_door" || r.status === "loading" || r.status === "unloading") key = "A · At door";
      else if (r.status === "loaded" || r.status === "empty") key = "B · Ready";
      else key = "C · Yard spots";
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(r);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  const advance = async (r: YardRow, next: YardRow["status"]) => {
    try { await statusFn({ data: { id: r.id, status: next } }); qc.invalidateQueries({ queryKey: ["yard-trailers"] }); }
    catch (e) { toast.error((e as Error).message); }
  };
  const remove = async (id: string) => {
    if (!confirm("Remove this trailer record?")) return;
    await delFn({ data: { id } });
    qc.invalidateQueries({ queryKey: ["yard-trailers"] });
  };

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Yard"
        description="Trailers on site — spots, doors, seals and dwell time"
        actions={
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />Check in trailer
          </Button>
        }
      />
      <DockYardTabs />

      <div className="px-4 sm:px-6 pt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label="On yard" value={totals.onYard} icon={<Container className="h-3.5 w-3.5" />} />
        <Stat label="Inbound" value={totals.inbound} icon={<ArrowDown className="h-3.5 w-3.5" />} />
        <Stat label="Outbound" value={totals.outbound} icon={<ArrowUp className="h-3.5 w-3.5" />} />
        <Stat label="At door" value={totals.atDoor} icon={<DoorOpen className="h-3.5 w-3.5" />} />
        <Stat label="Reefer" value={totals.reefer} icon={<Snowflake className="h-3.5 w-3.5" />} />
      </div>

      <div className="px-4 sm:px-6 pt-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Kind</Label>
          <Select value={kindFilter} onValueChange={(v) => setKindFilter(v as "all" | "inbound" | "outbound")}>
            <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="inbound">Inbound</SelectItem>
              <SelectItem value="outbound">Outbound</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as "all" | YardRow["status"]) }>
            <SelectTrigger className="h-9 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <label className="text-xs flex items-center gap-2 pb-2">
          <input type="checkbox" checked={includeDeparted} onChange={(e) => setIncludeDeparted(e.target.checked)} />
          Include departed
        </label>
        <div className="pb-1 flex items-end gap-2">
          <DateRangeFilter
            value={{ start: urlSearch.start, end: urlSearch.end }}
            onChange={(r) => navigate({ search: (p: typeof urlSearch) => ({ ...p, start: r.start, end: r.end }) })}
          />
          <SortDirectionToggle
            value={sort}
            onChange={(s) => navigate({ search: (p: typeof urlSearch) => ({ ...p, sort: s === "desc" ? undefined : s }) })}
          />
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-4">
        {isLoading ? (
          <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">Loading…</CardContent></Card>
        ) : groups.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">No trailers on yard.</CardContent></Card>
        ) : groups.map(([label, items]) => (
          <Card key={label}>
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-medium">
                {label.replace(/^[A-Z] · /, "")} <span className="text-muted-foreground font-normal">· {items.length}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Spot</TableHead>
                    <TableHead>Trailer</TableHead>
                    <TableHead className="w-24">Kind</TableHead>
                    <TableHead>Carrier / Seal</TableHead>
                    <TableHead className="w-28">Status</TableHead>
                    <TableHead className="w-20">Door</TableHead>
                    <TableHead className="w-24">Dwell</TableHead>
                    <TableHead>Driver</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => (
                    <TableRow key={r.id} className="text-sm">
                      <TableCell>
                        <div className="font-mono inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          {r.yard_spot ?? "—"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <button className="text-left hover:underline" onClick={() => { setEditing(r); setDialogOpen(true); }}>
                          <div className="font-mono font-medium">{r.trailer_number}</div>
                          {r.temp_setpoint_f != null && (
                            <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                              <Snowflake className="h-3 w-3" />{r.temp_setpoint_f}°F
                            </div>
                          )}
                        </button>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-normal">
                          {r.kind === "inbound" ? <ArrowDown className="h-3 w-3 mr-1" /> : <ArrowUp className="h-3 w-3 mr-1" />}
                          {r.kind}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>{r.carrier ?? "—"}</div>
                        <div className="text-xs text-muted-foreground font-mono">{r.seal_number ? `seal ${r.seal_number}` : "—"}</div>
                      </TableCell>
                      <TableCell><StatusPill tone={statusTone(r.status)} dot>{r.status.replace("_", " ")}</StatusPill></TableCell>
                      <TableCell className="font-mono text-xs">{r.dock_door ?? "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {r.departed_at ? "—" : fmtDuration(r.arrived_at)}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{r.driver_name ?? "—"}</div>
                        <div className="text-muted-foreground font-mono">{r.driver_phone ?? ""}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {!r.departed_at && r.status !== "at_door" && (
                            <Button size="sm" variant="outline" onClick={() => advance(r, "at_door")}>
                              <DoorOpen className="h-3.5 w-3.5 mr-1" />To door
                            </Button>
                          )}
                          {!r.departed_at && (
                            <Button size="sm" onClick={() => advance(r, "departed")}>
                              <LogOut className="h-3.5 w-3.5 mr-1" />Depart
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => remove(r.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))}
      </div>

      <YardEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        tenantId={tenantId}
        facilityId={facilityId}
        onSave={async (payload) => {
          await saveFn({ data: payload });
          qc.invalidateQueries({ queryKey: ["yard-trailers"] });
        }}
      />
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: number; icon?: React.ReactNode }) {
  return (
    <div className="rounded-md border bg-card px-3 py-2">
      <div className="text-xs text-muted-foreground flex items-center gap-1">{icon}{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

type SavePayload = {
  id?: string;
  tenantId: string;
  facilityId: string;
  trailerNumber: string;
  carrier: string | null;
  sealNumber: string | null;
  yardSpot: string | null;
  dockDoor: string | null;
  kind: "inbound" | "outbound";
  status?: YardRow["status"];
  tempSetpointF: number | null;
  driverName: string | null;
  driverPhone: string | null;
  notes: string | null;
};

function YardEditDialog({
  open, onOpenChange, editing, tenantId, facilityId, onSave,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  editing: YardRow | null;
  tenantId: string | null;
  facilityId: string | null;
  onSave: (p: SavePayload) => Promise<void>;
}) {
  const isEdit = !!editing;
  const [kind, setKind] = useState<"inbound" | "outbound">(editing?.kind ?? "inbound");
  const [trailer, setTrailer] = useState(editing?.trailer_number ?? "");
  const [carrier, setCarrier] = useState(editing?.carrier ?? "");
  const [seal, setSeal] = useState(editing?.seal_number ?? "");
  const [spot, setSpot] = useState(editing?.yard_spot ?? "");
  const [door, setDoor] = useState(editing?.dock_door ?? "");
  const [status, setStatus] = useState<YardRow["status"]>(editing?.status ?? "arrived");
  const [temp, setTemp] = useState<string>(editing?.temp_setpoint_f != null ? String(editing.temp_setpoint_f) : "");
  const [driver, setDriver] = useState(editing?.driver_name ?? "");
  const [phone, setPhone] = useState(editing?.driver_phone ?? "");
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKind(editing?.kind ?? "inbound");
    setTrailer(editing?.trailer_number ?? "");
    setCarrier(editing?.carrier ?? "");
    setSeal(editing?.seal_number ?? "");
    setSpot(editing?.yard_spot ?? "");
    setDoor(editing?.dock_door ?? "");
    setStatus(editing?.status ?? "arrived");
    setTemp(editing?.temp_setpoint_f != null ? String(editing.temp_setpoint_f) : "");
    setDriver(editing?.driver_name ?? "");
    setPhone(editing?.driver_phone ?? "");
    setNotes(editing?.notes ?? "");
  }, [open, editing]);

  const submit = async () => {
    if (!tenantId || !facilityId) { toast.error("Select a tenant & facility first"); return; }
    if (!trailer.trim()) { toast.error("Trailer number required"); return; }
    setBusy(true);
    try {
      const tempNum = temp.trim() === "" ? null : Number(temp);
      await onSave({
        id: editing?.id,
        tenantId, facilityId,
        trailerNumber: trailer.trim(),
        carrier: carrier || null,
        sealNumber: seal || null,
        yardSpot: spot || null,
        dockDoor: door || null,
        kind,
        status,
        tempSetpointF: Number.isFinite(tempNum as number) ? tempNum : null,
        driverName: driver || null,
        driverPhone: phone || null,
        notes: notes || null,
      });
      toast.success(isEdit ? "Updated" : "Checked in");
      onOpenChange(false);
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{isEdit ? "Edit trailer" : "Check in trailer"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Trailer #</Label>
            <Input value={trailer} onChange={(e) => setTrailer(e.target.value)} placeholder="T-44892" />
          </div>
          <div>
            <Label className="text-xs">Kind</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as "inbound" | "outbound")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="outbound">Outbound</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Carrier</Label>
            <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="ODFL" />
          </div>
          <div>
            <Label className="text-xs">Seal #</Label>
            <Input value={seal} onChange={(e) => setSeal(e.target.value)} placeholder="11023" />
          </div>
          <div>
            <Label className="text-xs">Yard spot</Label>
            <Input value={spot} onChange={(e) => setSpot(e.target.value)} placeholder="Y-14" />
          </div>
          <div>
            <Label className="text-xs">Door (if at door)</Label>
            <Input value={door} onChange={(e) => setDoor(e.target.value)} placeholder="D-7" />
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as YardRow["status"]) }>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Reefer setpoint (°F)</Label>
            <Input value={temp} onChange={(e) => setTemp(e.target.value)} placeholder="34" inputMode="decimal" />
          </div>
          <div>
            <Label className="text-xs">Driver</Label>
            <Input value={driver} onChange={(e) => setDriver(e.target.value)} placeholder="Jane Doe" />
          </div>
          <div>
            <Label className="text-xs">Driver phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="555-0101" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Bobtail, drop, lift-gate, hazmat..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {isEdit ? "Save" : "Check in"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}